/**
 * AdminLearningPanel — «Capítulos de aprendizaje» (Envío AP-1).
 *
 * Lista los capítulos (orden, estado, «en onboarding»), permite crear,
 * editar, reordenar y borrar. El video y la miniatura se eligen de la
 * galería del admin o se pegan como URL; el servidor los COPIA al
 * almacenamiento protegido (platform/learning/), así que no se pierden
 * aunque el asset original se borre.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, GraduationCap, Loader2, Lock, Plus, RefreshCw, Save, Trash2, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { learningService } from '../../services/learningService';
import { galleryService } from '../../services/galleryService';
import { formatDuration, youtubeEmbedUrl, youtubeVideoId } from '../learning/learningModel';
import { YouTubeThumbnail } from '../learning/LearningCenter';
import YouTubeAdminPreview from './YouTubeAdminPreview';
import './admin-learning.css';

const STATUS = { published: 'Publicado', soon: 'Próximamente', draft: 'Borrador' };
const TOPICS = ['Primeros pasos', 'Canvas', 'LMX', 'Edición', 'Marca', 'Publicar', 'MCP', 'Créditos'];
const EMPTY = {
  title: '', description: '', topic: 'Primeros pasos', status: 'draft',
  show_in_onboarding: true, featured: false, video_source: 'r2', video_url: '', thumb_url: '', duration_seconds: '',
};

const toForm = (c) => ({
  title: c.title || '', description: c.description || '', topic: c.topic || 'Primeros pasos',
  status: c.status || 'draft', show_in_onboarding: !!c.show_in_onboarding, featured: !!c.featured,
  video_source: c.video_source || 'r2',
  video_url: c.video_url || '', thumb_url: c.thumb_url || '',
  duration_seconds: c.duration_seconds ?? '',
});

/** Mide la duración de un video desde el navegador (sin descargarlo entero). */
const measureDuration = (url) => new Promise((resolve) => {
  if (!url) { resolve(null); return; }
  const v = document.createElement('video');
  const done = (value) => { v.removeAttribute('src'); v.load(); resolve(value); };
  v.preload = 'metadata';
  v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? Math.round(v.duration * 10) / 10 : null);
  v.onerror = () => done(null);
  setTimeout(() => done(null), 8000);
  v.src = url;
});

export default function AdminLearningPanel() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // id | 'new' | null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState([]);
  const [images, setImages] = useState([]);
  const dragId = useRef(null);
  const durationManualRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await learningService.adminList();
      setChapters(data.chapters || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los capítulos (¿falta la migración 051?)');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    galleryService.listAssets({ assetType: 'video', limit: 40 })
      .then((d) => setVideos(d.assets || [])).catch(() => {});
    galleryService.listAssets({ assetType: 'image', limit: 40 })
      .then((d) => setImages(d.assets || [])).catch(() => {});
  }, []);

  const current = useMemo(() => chapters.find((c) => c.id === editing) || null, [chapters, editing]);

  const startEdit = (chapter) => { durationManualRef.current = false; setEditing(chapter.id); setForm(toForm(chapter)); };
  const startNew = () => { durationManualRef.current = false; setEditing('new'); setForm(EMPTY); };
  const set = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : (e?.target ? e.target.value : e);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const pickVideo = async (url) => {
    setForm((f) => ({ ...f, video_source: 'r2', video_url: url }));
    const asset = videos.find((a) => a.url === url);
    if (asset?.thumbnail_url) setForm((f) => ({ ...f, thumb_url: f.thumb_url || asset.thumbnail_url }));
    const seconds = await measureDuration(url);
    if (seconds) setForm((f) => ({ ...f, duration_seconds: seconds }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('El capítulo necesita un título'); return; }
    if (form.status === 'published' && !form.video_url) {
      toast.error('Para publicarlo elegí un video'); return;
    }
    if (form.video_source === 'youtube' && form.video_url && !youtubeVideoId(form.video_url)) {
      toast.error('Ingresá una URL válida de YouTube'); return;
    }
    setBusy(true);
    const values = {
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
      duration_seconds: form.duration_seconds === '' ? undefined : Number(form.duration_seconds),
    };
    try {
      if (editing === 'new') {
        const res = await learningService.adminCreate(values);
        setEditing(res.chapter?.id || null);
        toast.success('Capítulo creado');
      } else {
        await learningService.adminUpdate(editing, values);
        toast.success('Cambios guardados');
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!current) return;
    if (!window.confirm(`¿Borrar «${current.title}»? Se borra también su copia protegida del video.`)) return;
    setBusy(true);
    try {
      await learningService.adminDelete(current.id);
      toast.success('Capítulo borrado');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo borrar');
    } finally { setBusy(false); }
  };

  const persistOrder = async (ordered) => {
    setChapters(ordered);
    try { await learningService.adminReorder(ordered.map((c) => c.id)); } catch {
      toast.error('No se pudo guardar el orden'); load();
    }
  };
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= chapters.length) return;
    const next = [...chapters];
    [next[index], next[target]] = [next[target], next[index]];
    persistOrder(next);
  };
  const onDrop = (overId) => {
    const from = chapters.findIndex((c) => c.id === dragId.current);
    const to = chapters.findIndex((c) => c.id === overId);
    dragId.current = null;
    if (from < 0 || to < 0 || from === to) return;
    const next = [...chapters];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    persistOrder(next);
  };

  const toggleOnboarding = async (chapter) => {
    setChapters((list) => list.map((c) => (c.id === chapter.id
      ? { ...c, show_in_onboarding: !c.show_in_onboarding } : c)));
    try {
      await learningService.adminUpdate(chapter.id, { show_in_onboarding: !chapter.show_in_onboarding });
    } catch { toast.error('No se pudo cambiar'); load(); }
  };

  return (
    <div className="sml-adl" data-testid="admin-learning-panel">
      <div className="sml-adl-main">
        <header className="sml-adl-head">
          <div>
            <h3><GraduationCap size={18} aria-hidden="true" /> Capítulos de aprendizaje</h3>
            <p>Se muestran al iniciar, en el último paso del recorrido y en ? › Videos instructivos.</p>
          </div>
          <div className="sml-adl-head-actions">
            <button type="button" className="sml-adl-icon" onClick={load} aria-label="Actualizar" disabled={loading}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            </button>
            <button type="button" className="sml-adl-primary" onClick={startNew} data-testid="admin-learning-new">
              <Plus size={15} aria-hidden="true" /> Nuevo capítulo
            </button>
          </div>
        </header>

        <table className="sml-adl-table">
          <thead>
            <tr>
              <th aria-label="Orden" />
              <th>Capítulo</th>
              <th>Tema</th>
              <th>Duración</th>
              <th>Estado</th>
              <th>Onboarding</th>
            </tr>
          </thead>
          <tbody>
            {chapters.map((c, i) => (
              <tr key={c.id}
                  className={editing === c.id ? 'is-active' : ''}
                  draggable
                  onDragStart={() => { dragId.current = c.id; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(c.id)}
                  data-testid={`admin-learning-row-${c.number}`}>
                <td className="sml-adl-order">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                          aria-label={`Subir ${c.title}`}><ArrowUp size={13} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === chapters.length - 1}
                          aria-label={`Bajar ${c.title}`}><ArrowDown size={13} /></button>
                </td>
                <td>
                  <button type="button" className="sml-adl-chapter" onClick={() => startEdit(c)}>
                    <YouTubeThumbnail chapter={c}
                      fallback={<span className="sml-adl-thumb-empty">{String(c.number).padStart(2, '0')}</span>} />
                    <span>
                      <b>{c.title}</b>
                      <small>
                        Ep. {String(c.number).padStart(2, '0')}
                        {c.featured && ' · destacado'}
                        {c.video_source === 'youtube'
                          ? <span className="sml-adl-lock"><Unlock size={11} aria-hidden="true" /> YouTube</span>
                          : c.video_url && (c.protected
                          ? <span className="sml-adl-lock"><Lock size={11} aria-hidden="true" /> protegido</span>
                          : <span className="sml-adl-lock is-warn"><Unlock size={11} aria-hidden="true" /> sin proteger</span>)}
                      </small>
                    </span>
                  </button>
                </td>
                <td>{c.topic}</td>
                <td>{formatDuration(c.duration_seconds)}</td>
                <td><span className={`sml-adl-status is-${c.status}`}>{STATUS[c.status] || c.status}</span></td>
                <td>
                  <label className="sml-adl-switch">
                    <input type="checkbox" checked={!!c.show_in_onboarding} onChange={() => toggleOnboarding(c)}
                           aria-label={`Mostrar ${c.title} en el inicio`} />
                    <span aria-hidden="true" />
                  </label>
                </td>
              </tr>
            ))}
            {!loading && chapters.length === 0 && (
              <tr><td colSpan={6} className="sml-adl-empty">Todavía no hay capítulos. Creá el primero.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <aside className="sml-adl-edit" aria-labelledby="sml-adl-edit-title">
          <div className="sml-adl-edit-head">
            <h4 id="sml-adl-edit-title">{editing === 'new' ? 'Nuevo capítulo' : `Editar Ep. ${String(current?.number ?? '').padStart(2, '0')}`}</h4>
            {current && <span className={`sml-adl-status is-${current.status}`}>{STATUS[current.status]}</span>}
          </div>

          <label>Título
            <input value={form.title} onChange={set('title')} maxLength={120} data-testid="admin-learning-title" />
          </label>
          <label>Descripción
            <textarea rows={3} value={form.description} onChange={set('description')} maxLength={600} />
          </label>

          <fieldset className="sml-adl-source">
            <legend>Origen del video</legend>
            <label><input type="radio" name="video-source" value="r2" checked={form.video_source === 'r2'}
                          onChange={set('video_source')} /> Galería de Studio Media Labs</label>
            <label><input type="radio" name="video-source" value="youtube" checked={form.video_source === 'youtube'}
                          onChange={set('video_source')} /> YouTube</label>
          </fieldset>
          {form.video_source === 'r2' ? (
            <>
              <label>Video (de tu galería)
                <select value={videos.some((v) => v.url === form.video_url) ? form.video_url : ''}
                        onChange={(e) => e.target.value && pickVideo(e.target.value)}>
                  <option value="">— Elegí un video —</option>
                  {videos.map((v) => <option key={v.id} value={v.url}>{v.filename || v.id}</option>)}
                </select>
              </label>
              <label>… o pegá la URL del video
                <input value={form.video_url} onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                       onBlur={(e) => e.target.value && pickVideo(e.target.value)} placeholder="https://…mp4" />
              </label>
            </>
          ) : (
            <label>URL de YouTube
              <input value={form.video_url} onChange={(e) => {
                       durationManualRef.current = false;
                       setForm((f) => ({ ...f, video_url: e.target.value, duration_seconds: '' }));
                     }}
                     placeholder="https://youtu.be/VIDEO_ID" aria-invalid={!!form.video_url && !youtubeVideoId(form.video_url)} />
            </label>
          )}
          {form.video_url && (
            form.video_source === 'youtube' && youtubeEmbedUrl(form.video_url)
              ? <YouTubeAdminPreview url={form.video_url} customThumb={form.thumb_url}
                    onDuration={(seconds) => {
                      if (!durationManualRef.current) {
                        setForm((f) => ({ ...f, duration_seconds: seconds }));
                      }
                    }} />
              : form.video_source === 'r2' && <video className="sml-adl-preview" src={form.video_url}
                  poster={form.thumb_url || undefined} controls preload="metadata" />
          )}

          <div className="sml-adl-grid">
            <label>Tema
              <select value={form.topic} onChange={set('topic')}>
                {[...new Set([...TOPICS, form.topic])].map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label>Estado
              <select value={form.status} onChange={set('status')}>
                <option value="published">Publicado</option>
                <option value="soon">Próximamente</option>
                <option value="draft">Borrador</option>
              </select>
            </label>
            <label>Miniatura
              <select value={images.some((a) => a.url === form.thumb_url) ? form.thumb_url : ''}
                      onChange={(e) => setForm((f) => ({ ...f, thumb_url: e.target.value }))}>
                <option value="">{form.thumb_url ? 'La actual' : '— Sin miniatura —'}</option>
                {images.map((a) => <option key={a.id} value={a.url}>{a.filename || a.id}</option>)}
              </select>
            </label>
            <label>Duración (s)
              <input type="number" min="0" step="0.1" value={form.duration_seconds}
                     onChange={(e) => {
                       durationManualRef.current = true;
                       setForm((f) => ({ ...f, duration_seconds: e.target.value }));
                     }} />
              <small>{formatDuration(form.duration_seconds)} · editable si falla la detección automática</small>
            </label>
          </div>

          <label className="sml-adl-check">
            <input type="checkbox" checked={form.show_in_onboarding} onChange={set('show_in_onboarding')} />
            Mostrar en el inicio (onboarding)
          </label>
          <label className="sml-adl-check">
            <input type="checkbox" checked={form.featured} onChange={set('featured')} />
            Capítulo destacado (se reproduce primero)
          </label>
          <p className="sml-adl-note"><Lock size={12} aria-hidden="true" /> {form.video_source === 'youtube'
            ? 'El video queda como referencia externa; solo la miniatura personalizada se copia al almacenamiento protegido.'
            : 'Al guardar, el video y la miniatura se copian al almacenamiento protegido: no vencen ni se borran con la galería.'}</p>

          <div className="sml-adl-edit-actions">
            {current && (
              <button type="button" className="sml-adl-danger" onClick={remove} disabled={busy}>
                <Trash2 size={14} aria-hidden="true" /> Borrar
              </button>
            )}
            <span />
            <button type="button" className="sml-adl-ghost" onClick={() => setEditing(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="sml-adl-primary" onClick={save} disabled={busy}
                    data-testid="admin-learning-save">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} aria-hidden="true" />} Guardar
            </button>
          </div>
        </aside>
      )}
    </div>
  );
}
